<script>
import {
  MaxPreferredQueueSize,
  SelectEducatorsDragGroup,
} from '@/shared/constants/booking/BookingSelectEducators'
import draggable from 'vuedraggable'
import Colours from '@/shared/constants/design/Colours.js'
import { mapActions, mapGetters } from 'vuex'
import CandidateDetailsModal from '@/components/candidate/detailsPopup/candidate-details-modal.vue'
import PreferredEducatorCard from './preferred-educator-card.vue'

const { preferredQueue, availableEducators } = SelectEducatorsDragGroup

export default {
  name: 'PreferredEducatorQueue',
  components: { PreferredEducatorCard, CandidateDetailsModal, draggable },
  props: {
    bookingDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      Colours,
      selectedCandidateId: null,
      showCandidateModal: false,
      queueGroup: {
        name: preferredQueue,
        pull: 'clone',
        put: [availableEducators],
      },
    }
  },
  computed: {
    ...mapGetters({
      isQueueFull: 'bookingCreate/isQueueFull',
      bookingTimeZone: 'bookingCreate/bookingTimeZone',
    }),
    maxQueueSize: () => MaxPreferredQueueSize,
    preferredQueue: {
      get() {
        return this.$store.getters['bookingCreate/preferredQueue']
      },
      set(newQueue) {
        this.setPreferredQueue(newQueue)
      },
    },
  },
  watch: {
    isQueueFull(isFull) {
      if (isFull) {
        this.$toast.warning(
          this.$t('bookingCreateV2.selectEducatorsStep.queueFullWarningToast')
        )
      }
    },
  },
  methods: {
    ...mapActions({
      setPreferredQueue: 'bookingCreate/setPreferredQueue',
      setPreferredQueueDragging: 'bookingCreate/setPreferredQueueDragging',
      moveEducatorUp: 'bookingCreate/moveEducatorUp',
      moveEducatorDown: 'bookingCreate/moveEducatorDown',
      removeFromQueue: 'bookingCreate/removeFromQueue',
    }),
    onQueueMove(event) {
      const queueElement = this.$refs?.preferredQueue?.$el
      const isMovingIntoQueue =
        !queueElement ||
        (event.from !== queueElement && event.to === queueElement)

      if (this.isQueueFull && isMovingIntoQueue) return false

      return true
    },
    onQueueClone({ clone }) {
      clone.classList.add('queue-source-clone')
      clone.setAttribute('aria-hidden', 'true')
      clone.setAttribute('inert', '')
    },
    showCandidateDialog(candidateId) {
      this.selectedCandidateId = candidateId
      this.showCandidateModal = true
    },
  },
}
</script>

<template>
  <v-card
    class="fill-height d-flex flex-column p-lg rounded-lg"
    data-testid="preferred-educator-queue"
  >
    <div class="mb-3">
      <h3 class="display-xs mb-1">
        {{ $t('bookingCreateV2.selectEducatorsStep.preferredOrderTitle') }}
      </h3>
      <p class="text-xs-regular neutrals-grey-500-text mb-0">
        {{
          $t('bookingCreateV2.selectEducatorsStep.preferredOrderDescription')
        }}
      </p>
    </div>

    <div
      class="preferred-queue d-flex flex-column pa-2 overflow-x-hidden overflow-y-auto rounded-lg border-grey-200 border-dashed border-width-2 p-relative"
    >
      <div
        v-if="!preferredQueue.length"
        class="queue-empty-state d-flex flex-column align-center justify-center pa-6 rounded-xl"
        data-testid="queue-empty-state"
      >
        <p class="text-md-bold mb-2">{{
          $t('bookingCreateV2.selectEducatorsStep.queueEmptyTitle')
        }}</p>
        <p class="text-md neutrals-grey-500-text text-center mb-0">
          {{
            $t('bookingCreateV2.selectEducatorsStep.queueEmptyHint', [
              $t('app.companyNameShort'),
            ])
          }}
        </p>
      </div>
      <draggable
        ref="preferredQueue"
        v-model="preferredQueue"
        no-transition-on-drag
        :remove-clone-on-hide="false"
        :group="queueGroup"
        :move="onQueueMove"
        filter=".v-btn"
        :prevent-on-filter="false"
        :animation="300"
        ghost-class="queue-drop-ghost"
        class="reorderable-list d-flex flex-column flex-grow-1 flex-shrink-0 p-relative"
        @clone="onQueueClone"
        @start="setPreferredQueueDragging(true)"
        @end="setPreferredQueueDragging(false)"
      >
        <transition-group
          tag="div"
          name="slide-fade-list"
          class="flex-grow-1 flex-shrink-0"
        >
          <PreferredEducatorCard
            v-for="(educator, index) in preferredQueue"
            :key="educator.id"
            class="mb-2"
            :educator="educator"
            :index="index"
            :is-first="index === 0"
            :is-last="index === preferredQueue.length - 1"
            @move-up="moveEducatorUp(index)"
            @move-down="moveEducatorDown(index)"
            @remove="removeFromQueue(educator.id)"
            @view-candidate="showCandidateDialog"
          />
        </transition-group>
      </draggable>
    </div>
    <v-alert
      :class="[isQueueFull ? 'opacity-100' : 'opacity-0']"
      :color="Colours.brandBlue25"
      text
      dense
      class="transition-opacity rounded-lg brand-blue-600-text mt-4 mb-0"
      data-testid="queue-full-alert"
    >
      {{
        $t('bookingCreateV2.selectEducatorsStep.queueFullMessage', [
          maxQueueSize,
        ])
      }}
    </v-alert>

    <CandidateDetailsModal
      v-if="showCandidateModal"
      :id="selectedCandidateId"
      v-model="showCandidateModal"
      :booking-date="bookingDate"
      :booking-time-zone="bookingTimeZone"
      @closeModal="showCandidateModal = false"
    />
  </v-card>
</template>

<style lang="scss" scoped>
@import '@/styles/_r2b-colours.scss';
@import '@/styles/_vue-transitions.scss';

.queue-empty-state {
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;
}

.reorderable-list {
  z-index: 2;
}

.preferred-queue {
  min-height: 0;
}

::v-deep .queue-drop-ghost {
  height: 5px;
  padding: 0;
  margin: 4px 0;
  overflow: hidden;
  background-color: $brand-blue-400;
  border: none;
  border-radius: 8px;

  > * {
    visibility: hidden;
  }
}
</style>
